<template>
  <div class="studio-content" ref="contentRoot">

    <!-- Sign-image builder — the same AltarBuilder form the Altar Mirror uses,
         here painting the reader's OWN Sun/Rising sign image. Opened from the
         Mystic → Identity sign-card pencils. Now in the shared Conjure-style modal. -->
    <StudioEditModal :open="signBuilderOpen" :title="signLabel ? `Paint your ${signLabel}` : 'Paint your sign'"
                     :right-offset="modalRight" :max-width="760" :align="huggingChat ? 'start' : 'center'" @close="closeSignBuilder">
      <AltarBuilder :subject="signSubject" :label="signLabel" embedded @done="closeSignBuilder" />
    </StudioEditModal>

    <!-- Tab Strip -->
    <div class="studio-tabs">
      <button
        v-for="tab in tabs" :key="tab.key"
        class="studio-tab"
        :class="{ active: activeTab === tab.key }"
        :data-testid="`studio-tab-${tab.key}`"
        @click="switchTab(tab.key)"
      >{{ tab.label }}</button>
    </div>

    <!-- Superuser-only: edit any non-keeper reader in the Studio. Sits in the
         content (above the Dashboard avatar), NOT in the app's top-right chrome.
         Switching is in-place + Studio-scoped (see switchReader). -->
    <div v-if="store.reader?.is_platform_admin" class="studio-admin-bar">
      <span v-if="store.studioActingAsReader" class="studio-acting-chip">✦ Editing as {{ store.reader?.name }}</span>
      <select
        class="studio-reader-switcher"
        :value="store.studioActingAsReader"
        :disabled="switchingReader"
        @change="switchReader($event.target.value)"
        title="Edit another reader (admin)"
      >
        <option value="">↩ My own Studio</option>
        <optgroup label="Edit a reader (admin)">
          <option v-for="r in studioReaders" :key="r.sub_domain" :value="r.sub_domain">
            {{ r.name }}{{ r.not_listed_in_community ? ' · private' : '' }}
          </option>
        </optgroup>
      </select>
    </div>

    <!-- Panel Content. Transition removed 2026-05-07 — panel-fade with
         mode="out-in" was blanking on tab switches; if any leaving
         panel's leave-hook stalls (component unmount taking a frame,
         async cleanup), the entering panel never mounts and the area
         just sits empty. Direct v-if swap is reliable. -->
    <div class="studio-panel-area" :key="panelKey">
      <OverviewPanel      v-if="activeTab === 'overview'"   key="overview"   @navigate="switchTab($event)" />
      <ProfilePanel       v-else-if="activeTab === 'profile'"  key="profile" ref="profileRef" @sub-changed="onProfileSubChanged" />
      <ReadingSessionPanel
        v-else-if="activeTab === 'reading_session'"
        key="reading_session"
      />
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { store } from '@/store'
import { getStudioReaders, getMyReader, listSkillVariants } from '@/api-service'
import OverviewPanel from './panels/OverviewPanel.vue'
import ProfilePanel from './panels/ProfilePanel.vue'
import ReadingSessionPanel from './panels/ReadingSessionPanel.vue'
import AltarBuilder from '@/components/ui/AltarBuilder.vue'
import StudioEditModal from './StudioEditModal.vue'
import { signSubject as toSignSubject, signLabel as toSignLabel } from '@/utils/chart-signs'

// Conjure-style modal scrim starts at the studio content's left edge so the
// assistant pane stays visible (matches the ProfilePanel edit modals).
const contentRoot = ref(null)
const modalRight = ref(0)
const huggingChat = ref(false)
function measureModalRight() {
  // Chat floats on the RIGHT; anchor the scrim's right edge to the chat's LEFT
  // edge so the card hugs it (+10px gap). 0 = full width (centered) when no chat.
  const chat = document.querySelector('.ai-zone')
  if (chat) {
    const r = chat.getBoundingClientRect()
    if (r.width > 0 && r.left > 0) { modalRight.value = Math.round(window.innerWidth - r.left); huggingChat.value = true; return }
  }
  modalRight.value = 0
  huggingChat.value = false
}

defineProps({
  chatAction: { type: Object, default: null },
})
const emit = defineEmits(['studio-panel-changed', 'context-signal'])

// The Studio opens on the Mystic itself, not the dashboard: "My Mystic" is
// where the presentation, voice and chart live, which is what a Mystic's
// maker comes here to work on. ?studio= still restores any tab.
const activeTab = ref('profile')

// Identity tab removed Phase 3' — its remaining concerns (Voice +
// Privacy) live under "Advanced Settings" inside Profile.
// Skill Refactor Phase 9 — "Skills" tab replaced by "Reading Session":
// the new home for per-skill prompt authoring + variant pricing.
// Existing Skill Builder activation flow (deck picking, symbology
// pools, etc.) still works via its current navigation entry points.
// Settings folded into Syndicate (2026-06). The Syndicate tab is now shown to
// EVERY reader: all readers get the slim Daily Drop config (image + message),
// while syndicate_enabled ("super") readers additionally see the full
// syndication sections inside the panel.
const tabs = computed(() => {
  return [
    { key: 'overview',        label: 'Dashboard' },
    { key: 'profile',         label: 'My Mystic' },
    { key: 'reading_session', label: 'Reading Sessions' },
  ]
})

// Sub-tabs that live INSIDE the Mystic (profile) tab. Navigating to one opens
// the Mystic tab and selects the sub-tab. Syndication is its own top-level tab
// (it needs two levels of depth of its own), so it's NOT a profile sub-tab.
const PROFILE_SUBS = ['identity', 'voice', 'doorman']
const profileRef = ref(null)
const pendingSub = ref('')

function switchTab(key) {
  // Syndication folded into Mystic → Identity (drop-image pencil); send any legacy
  // nav to the Mystic tab.
  if (key === 'syndicate' || key === 'syndication') key = 'profile'
  if (PROFILE_SUBS.includes(key)) {
    activeTab.value = 'profile'
    if (profileRef.value) profileRef.value.switchSub(key)
    else pendingSub.value = key
    emit('studio-panel-changed', key)   // ?studio = the sub-tab
    return
  }
  activeTab.value = key
  emit('studio-panel-changed', key)
}

// User clicked a sub-tab inside the Mystic panel — keep ?studio in sync.
function onProfileSubChanged(key) {
  if (activeTab.value === 'profile') emit('studio-panel-changed', key)
}

// Apply a deep-link sub-tab once the Mystic panel has mounted.
watch(profileRef, (el) => {
  if (el && pendingSub.value) { el.switchSub(pendingSub.value); pendingSub.value = '' }
})

// ─── Sign-image builder ──────────────────────────────────────────────
// The Mystic → Identity sign cards (Sun / Moon / Rising) fire `open-sign-builder`
// (scope:'reader'). The Altar listens for the seeker's own version; here the
// Studio overlays the same AltarBuilder for the reader's own sign image.
// AltarBuilder keys off store.session_id, which in the Studio is the logged-in
// reader's own session (Reader extends Querent).
const signBuilderOpen = ref(false)
const signSubject = ref('reader_sun_sign')   // reader_{sun,moon,rising}_sign
const signLabel = ref('')                     // e.g. "Gemini Moon"
function onOpenSignBuilder(e) {
  const d = e.detail || {}
  if (d.scope && d.scope !== 'reader') return   // not ours (e.g. a seeker Mirror)
  // On a direct /studio refresh, querent_id is restored but session_id may not
  // be — AltarBuilder needs it, so restore from localStorage as the Altar does.
  if (!store.session_id) store.session_id = localStorage.getItem('session_id') || ''
  // Reader-facing subjects — a SEPARATE set from the Mystic's private Altar
  // sign images, so a reader's brand signs never collide with their personal ones.
  signSubject.value = toSignSubject(d.which, 'reader')
  signLabel.value = toSignLabel(d.sign, d.which)
  signBuilderOpen.value = true
  window.dispatchEvent(new CustomEvent('studio-editor-modal', { detail: { source: 'sign', open: true } }))
  nextTick(measureModalRight)
  setTimeout(measureModalRight, 80)
  setTimeout(measureModalRight, 420)
}
function closeSignBuilder() {
  signBuilderOpen.value = false
  window.dispatchEvent(new CustomEvent('studio-editor-modal', { detail: { source: 'sign', open: false } }))
  // Dismiss the scoped Digital Assistant the builder may have opened.
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: false } }))
}
// ── Superuser "edit another reader" picker (Studio ONLY, ephemeral) ──────
// store.studioActingAsReader gates the act-as header (axiosConfig). It is NEVER
// persisted, so a refresh resets to your own Studio, and it's cleared the moment
// this component unmounts (leaving the Studio) — impersonation can't escape into
// Altar/Quest. Switching is in-place (no reload): set the flag, re-load the
// reader as the new target, then remount the panels via panelKey.
const studioReaders = ref([])
const panelKey = ref(0)
const switchingReader = ref(false)

async function switchReader(subDomain) {
  if (switchingReader.value) return
  switchingReader.value = true
  store.studioActingAsReader = subDomain || ''   // '' → back to my own Studio
  try {
    // Drop the previous reader's SHARED studio state up front so nothing stale
    // (e.g. a variant id from the old reader) leaks into a request and 404s.
    store.readerSkillVariants = []
    await getMyReader()                                  // reader + skills (as target)
    store.readerSkillVariants = (await listSkillVariants()) || []  // variants (as target)
    panelKey.value++                                     // remount panels → re-fetch as target
  } catch (e) {
    console.error('Failed to switch studio reader:', e)
    store.studioActingAsReader = ''
    store.readerSkillVariants = []
    await getMyReader().catch(() => {})
  } finally {
    switchingReader.value = false
  }
}

onMounted(async () => {
  window.addEventListener('open-sign-builder', onOpenSignBuilder)
  window.addEventListener('resize', measureModalRight)
  measureModalRight()
  // The skill variants feed every tab (the Card Deck pane is a loop over
  // them), so the Studio loads them itself rather than trusting whichever
  // tab happens to open first to do it.
  if (!store.readerSkillVariants?.length) {
    try {
      store.readerSkillVariants = (await listSkillVariants()) || []
    } catch (e) {
      console.error('Failed to load skill variants:', e)
    }
  }
  if (store.reader?.is_platform_admin) {
    studioReaders.value = await getStudioReaders()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('open-sign-builder', onOpenSignBuilder)
  window.removeEventListener('resize', measureModalRight)
  // SAFETY: leaving the Studio must drop any impersonation so Altar/Quest/etc.
  // are always YOU. Clear the flag, drop the impersonated reader's shared state,
  // and restore your own reader into the store (variants reload fresh on the
  // next Studio entry — OverviewPanel rehydrates when readerSkillVariants empty).
  if (store.studioActingAsReader) {
    store.studioActingAsReader = ''
    store.readerSkillVariants = []
    getMyReader().catch(() => {})
  }
})

defineExpose({ switchTab })
</script>

<style scoped>
@import '@/styles/studio-forms.css';

/* Sign-image builder overlay — mirrors the Altar's builder pane. The studio
   root is position:relative, so inset:0 covers the panel area + tab strip. */
.studio-sign-builder-pane {
  position: absolute;
  inset: 0;
  z-index: 20;
  overflow-y: auto;
  background: #08000f;
  padding: 16px 14px 96px;
}

/* Superuser reader switcher — an in-content bar above the panel (above the
   Dashboard avatar). Deliberately NOT in the app's top-right chrome. */
.studio-admin-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;   /* directly above the avatar (which sits left) */
  gap: 8px;
  padding: 4px 16px 0;           /* tight top */
  max-width: 700px;              /* match the avatar card column width */
  /* Negative bottom pulls the bar down into the panel's 28px top padding so it
     sits snug above the avatar instead of floating with a big gap. */
  margin: 0 auto -16px;
}
.studio-reader-switcher {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #E8D8FF;
  background: rgba(20, 8, 40, 0.9);
  border: 1px solid rgba(195, 169, 255, 0.4);
  border-radius: 8px;
  padding: 5px 8px;
  cursor: pointer;
  max-width: 200px;
}
.studio-reader-switcher:hover { border-color: rgba(255, 223, 163, 0.6); }
.studio-acting-chip {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  color: #FFDFA3;
  background: rgba(240, 217, 163, 0.1);
  border: 1px solid rgba(240, 217, 163, 0.4);
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
}

.studio-panel-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  /* Reserve room at the bottom so the fixed bottom-nav bar
     (Altar/Community/Quest/Studio, ~64px tall) doesn't clip the
     last section of any panel. Affects every studio tab uniformly. */
  padding-bottom: 96px;
}
</style>
